import React from "react"
import colors from "@constants/colors"
import { Box, Text } from "@chakra-ui/react"
import MenuItemCheckboxGroup from "@components/MenuItemCheckboxGroup"
import RangeSlider from "@components/form/inputs/RangeSlider"
import { FilterType, productSliderFilters } from "../../common"
import { toggleKeyInArray } from "@utils/index"
import { bp } from "@utils/MediaQueries"
import _ from "lodash"

interface FilterProps {
  path: Array<string>
  lastClear?: number
  filters?: any
  availableFilters?: any
  activeFilters?: any
  onFilter?: (filters: any) => void
  onClearFilter?: () => void
}

const renderCheckbox = (path, key, items) => {
  return (
    <MenuItemCheckboxGroup
      key={`${path.join(", ")}-${key}`}
      label={key}
      items={items}
    />
  )
}

const renderSlider = (lastClear, path, key, label, min, max, onChange) => {
  return (
    <Box
      key={`${lastClear}-${path.join(", ")}-${key}`}
      w="100%"
      h="136px"
      p="20px 0"
      borderBottom="solid 1px #E2E8F0"
    >
      <Text
        mb="24px"
        fontFamily="Avenir"
        fontSize="16px"
        fontWeight="800"
        lineHeight="20px"
        color="dark"
      >
        {label}
      </Text>
      <RangeSlider
        min={min}
        max={max}
        onChange={onChange}
        decimalPlaces={label === "Rack Units" ? 0 : 2}
        isCurrency={label === "Filter by Price"}
      />
    </Box>
  )
}

const Filters: React.FC<FilterProps> = ({
  path,
  lastClear,
  filters,
  availableFilters,
  activeFilters,
  onFilter,
  onClearFilter,
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState<any>([])

  React.useEffect(() => {
    setSelectedFilters(activeFilters.filters)
  }, [path, filters, activeFilters])

  const formattedFilters = React.useMemo(() => {
    const data = {}

    if (filters && Object.keys(filters).length > 0) {
      const filtersToFormat = Object.keys(filters).filter(
        (k) =>
          filters[k] &&
          (k === "Series" || k === "Features" || filters[k].length > 1)
      )

      filtersToFormat.forEach((key) => {
        const dataProps = {} as any

        if (productSliderFilters.indexOf(key) !== -1) {
          dataProps.type = FilterType.SLIDER
          dataProps.label = key

          const range = filters[key].map((f) => parseFloat(f))
          dataProps.min = _.min(range)
          dataProps.max = _.max(range)
          dataProps.onChange = (values) => {
            const rangeValue = dataProps.max - dataProps.min
            const [min, max] = values
            let adjustedMin, adjustedMax

            if (min === 0) {
              adjustedMin = dataProps.min
            } else {
              adjustedMin = (min / 100) * rangeValue + dataProps.min
            }

            if (max === 100) {
              adjustedMax = dataProps.max
            } else {
              adjustedMax = (max / 100) * rangeValue + dataProps.min
            }

            const updatedFilters = {
              ...selectedFilters,
              [key]: {
                min: adjustedMin,
                max: adjustedMax,
                pristine:
                  adjustedMin === dataProps.min &&
                  adjustedMax === dataProps.max,
              },
            }

            setSelectedFilters(updatedFilters)

            if (onFilter) {
              onFilter(updatedFilters)
            }
          }
        } else {
          dataProps.type = FilterType.CHECKBOX
          dataProps.label = key
          dataProps.items = filters[key].map((f) => ({
            label: f,
            checked:
              selectedFilters[key] &&
              selectedFilters[key].values &&
              selectedFilters[key].values.indexOf(f) !== -1,
            disabled:
              availableFilters &&
              ((availableFilters[key] &&
                availableFilters[key].indexOf(f) === -1) ||
                !availableFilters[key]),
            onChange: () => {
              const updatedFilters = {
                ...selectedFilters,
                [key]: {
                  values: toggleKeyInArray(
                    f,
                    selectedFilters[key]?.values || []
                  ),
                },
              }

              setSelectedFilters(updatedFilters)

              if (onFilter) {
                onFilter(updatedFilters)
              }
            },
          }))
        }

        data[key] = dataProps
      })
    }

    return data
  }, [path, filters, availableFilters, activeFilters, selectedFilters])

  const FilterElements = React.useMemo(() => {
    return (
      (Object.keys(formattedFilters).length > 0 &&
        Object.keys(formattedFilters).map((key) =>
          formattedFilters[key].type === FilterType.SLIDER
            ? renderSlider(
                lastClear,
                path,
                key,
                formattedFilters[key].label,
                formattedFilters[key].min,
                formattedFilters[key].max,
                formattedFilters[key].onChange
              )
            : renderCheckbox(path, key, formattedFilters[key].items)
        )) ||
      []
    )
  }, [path, formattedFilters])

  const filtersToApply = Object.keys(activeFilters.filters).filter(
    (key) =>
      activeFilters.filters[key] &&
      ((!!activeFilters.filters[key].values &&
        activeFilters.filters[key].values!.length > 0) ||
        (activeFilters.filters[key].min && activeFilters.filters[key]?.max))
  )

  return (
    <Box p="22px 0" pr={bp("initial", null, "0p25col")}>
      {filtersToApply?.length > 0 && (
        <Box justifyContent="center" display="flex" width="100%">
          <Box
            as="button"
            height="36px"
            mb="32px"
            padding="0 24px"
            bg="black"
            rounded="lg"
            fontWeight="800"
            fontFamily="Avenir"
            fontSize="16px"
            color="white"
            _focus={{ boxShadow: "outline", outline: "none" }}
            _hover={{ bg: colors.blackButtonFocus }}
            onClick={() => {
              if (onClearFilter) {
                onClearFilter()
              }
            }}
          >
            Reset Filters
          </Box>
        </Box>
      )}
      {FilterElements.length > 0 && FilterElements}
    </Box>
  )
}

export default Filters
